/**
 * Public DMCA Takedown Notice form at `/dmca`.
 *
 * Why a form (not just an `abuse@…` mailto): the DMCA safe-harbor
 * requires us to (a) publish a designated agent, (b) capture six
 * specific elements from the complainant per 17 USC §512(c)(3), and
 * (c) give the complainant a structured receipt. A mailto fails all
 * three quality bars. Submitting this form POSTs to
 * `/api/dmca/notice`; admins triage from `/admin/dmca`.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ShieldWarningIcon as ShieldWarning, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

const REQUIRED_ATTESTATIONS = [
  { key: "good_faith_belief", label: "I have a good-faith belief that the use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law." },
  { key: "accuracy_under_penalty_of_perjury", label: "I swear, under penalty of perjury, that the information in this notification is accurate." },
  { key: "authorized_to_act", label: "I am the copyright owner, or am authorized to act on behalf of the owner, of an exclusive right that is allegedly infringed." },
];

export default function Dmca() {
  const [form, setForm] = useState({
    complainant_name: "",
    complainant_email: "",
    complainant_address: "",
    complainant_phone: "",
    work_description: "",
    infringing_url: "",
    infringing_description: "",
    signature: "",
    good_faith_belief: false,
    accuracy_under_penalty_of_perjury: false,
    authorized_to_act: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const allSwornChecked = REQUIRED_ATTESTATIONS.every((a) => form[a.key]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/dmca/notice", form);
      setSubmitted(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PublicScreen>
        <div className="max-w-2xl mx-auto py-20 px-6 text-center">
          <CheckCircle size={48} weight="fill" className="mx-auto text-[#0F8043]" />
          <h1 className="font-spectral text-3xl tracking-tight mt-4">Notice received</h1>
          <p className="text-sm text-[#3F3F3F] mt-3">
            Your DMCA takedown notice has been recorded. Reference id:
          </p>
          <p className="font-mono text-xs bg-[#FAFAFA] inline-block px-3 py-1.5 rounded-md border border-[#EAEAEA] mt-3" data-testid="dmca-receipt-id">
            {submitted.id}
          </p>
          <p className="text-sm text-[#666] mt-5 max-w-lg mx-auto leading-relaxed" style={{ fontVariantLigatures: "none" }}>
            Our team reviews every notice within <strong>5 business days</strong>. We've also emailed you a confirmation receipt with the same reference id. If material is removed, the alleged uploader gets a counter-notice opportunity per 17 USC &sect;512(g).
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 mt-7 rounded-full bg-[#0E0E0E] text-white px-5 py-2 text-sm font-semibold hover:bg-[#C1272D]" data-testid="dmca-home">
            Back to Armstrong K9 Registry <ArrowRight size={13} />
          </Link>
        </div>
      </PublicScreen>
    );
  }

  return (
    <PublicScreen>
      <Helmet>
        <title>{"DMCA Takedown Notice · Armstrong K9 Registry"}</title>
        <meta name="description" content="File a DMCA takedown notice with Armstrong K9 Registry. Required under 17 USC §512(c)(3)." />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex items-start gap-3 mb-2">
          <ShieldWarning size={22} weight="duotone" className="text-[#C1272D] mt-1" />
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Legal · DMCA</p>
            <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">Report a copyright infringement</h1>
          </div>
        </div>
        <p className="text-sm text-[#3F3F3F] leading-relaxed max-w-2xl mt-3" style={{ fontVariantLigatures: "none" }}>
          Use this form if you believe content on Armstrong K9 Registry infringes
          your copyright. We respond to every notice that meets the requirements
          of <strong>17 USC &sect;512(c)(3)</strong>. False claims may carry liability under
          &sect;512(f). For unrelated complaints, please use{" "}
          <Link to="/contact" className="underline">/contact</Link> instead.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-[#EAEAEA] rounded-2xl p-5 sm:p-6" data-testid="dmca-form">
          <Section title="Your contact information">
            <Field label="Full name" required>
              <Input value={form.complainant_name} onChange={(e) => update("complainant_name", e.target.value)} required data-testid="dmca-name" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.complainant_email} onChange={(e) => update("complainant_email", e.target.value)} required data-testid="dmca-email" />
            </Field>
            <Field label="Mailing address" required>
              <Textarea rows={2} value={form.complainant_address} onChange={(e) => update("complainant_address", e.target.value)} required data-testid="dmca-address" />
            </Field>
            <Field label="Phone (optional)">
              <Input value={form.complainant_phone} onChange={(e) => update("complainant_phone", e.target.value)} data-testid="dmca-phone" />
            </Field>
          </Section>

          <Section title="The copyrighted work">
            <Field label="Describe the work you own" required hint="Identify the copyrighted work you claim has been infringed. Be specific.">
              <Textarea rows={3} value={form.work_description} onChange={(e) => update("work_description", e.target.value)} required data-testid="dmca-work" />
            </Field>
          </Section>

          <Section title="The infringing material on Armstrong K9 Registry">
            <Field label="URL of the infringing material" required hint="Direct link to the dog profile, course, post, listing, or page where you saw your work.">
              <Input type="url" value={form.infringing_url} onChange={(e) => update("infringing_url", e.target.value)} placeholder="https://armstrongk9registry.com/..." required data-testid="dmca-url" />
            </Field>
            <Field label="Additional description (optional)">
              <Textarea rows={3} value={form.infringing_description} onChange={(e) => update("infringing_description", e.target.value)} data-testid="dmca-desc" />
            </Field>
          </Section>

          <Section title="Sworn statements (all required)">
            <div className="space-y-3">
              {REQUIRED_ATTESTATIONS.map((a) => (
                <label key={a.key} className="flex items-start gap-3 cursor-pointer" data-testid={`dmca-ack-${a.key}`}>
                  <Checkbox
                    checked={!!form[a.key]}
                    onCheckedChange={(v) => update(a.key, !!v)}
                    className="mt-0.5"
                  />
                  <span className="text-[13px] text-[#3F3F3F] leading-relaxed">{a.label}</span>
                </label>
              ))}
            </div>
          </Section>

          <Section title="Electronic signature">
            <Field label="Type your full legal name to sign" required>
              <Input value={form.signature} onChange={(e) => update("signature", e.target.value)} required data-testid="dmca-signature" />
            </Field>
          </Section>

          <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-[#888] max-w-md">
              Submitting this form constitutes a formal notice under DMCA §512(c)(3).
            </p>
            <Button
              type="submit"
              disabled={submitting || !allSwornChecked}
              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white font-semibold"
              data-testid="dmca-submit"
            >
              {submitting ? "Submitting…" : "Submit DMCA notice"} <ArrowRight size={13} className="ml-1" />
            </Button>
          </div>
        </form>

        <div className="mt-8 text-xs text-[#888] leading-relaxed">
          <p className="font-semibold text-[#3F3F3F] mb-1">Designated agent</p>
          <p>
            Armstrong K9 Registry, Attn: DMCA Agent. Mail and email contact via the
            address above. Form submissions through this page are the fastest path to
            review.
          </p>
        </div>
      </div>
    </PublicScreen>
  );
}

function PublicScreen({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] font-manrope text-ink">
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-256.png" alt="" className="h-8 w-8 rounded-full object-contain" />
            <span className="font-spectral text-base tracking-tight">Armstrong K9 Registry</span>
          </Link>
          <nav className="hidden sm:flex items-center gap-5 text-sm text-[#3F3F3F]">
            <Link to="/terms" className="hover:text-ink">Terms</Link>
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/contact" className="hover:text-ink">Contact</Link>
          </nav>
        </div>
      </header>
      {children}
      <footer className="border-t border-[#E5E5E5] bg-white mt-12">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 text-xs text-[#888] flex flex-wrap items-center justify-between gap-3">
          <span>© Armstrong K9 Registry</span>
          <span>DMCA notice form &middot; 17 USC &sect;512(c)(3)</span>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-spectral text-base tracking-tight mb-3 text-[#0E0E0E]">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-[12.5px]">
        {label} {required && <span className="text-[#C1272D]">*</span>}
      </Label>
      {hint && <p className="text-[11px] text-[#888] mt-0.5 mb-1.5">{hint}</p>}
      {children}
    </div>
  );
}
